import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  LogOut, Trash2, Upload, Mail, Image as ImageIcon, Inbox, Loader2, Package, RefreshCw,
} from "lucide-react";
import {
  login, setToken, getToken, clearToken,
  getEnquiries, deleteEnquiry, getGallery, addGallery, deleteGallery,
  getProducts, updateProduct,
} from "../lib/api";
import { useToast } from "../hooks/use-toast";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const Admin = () => {
  const { toast } = useToast();
  const [authed, setAuthed] = useState(!!getToken());
  const [password, setPassword] = useState("");
  const [tab, setTab] = useState("enquiries");
  const [enquiries, setEnquiries] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ title: "", sector: "Residential", excerpt: "", image: "" });
  const [uploading, setUploading] = useState(false);
  const [savingId, setSavingId] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [e, g, p] = await Promise.all([getEnquiries(), getGallery(), getProducts()]);
      setEnquiries(e.data);
      setGallery(g.data);
      setProducts(p.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        clearToken();
        setAuthed(false);
      }
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (authed) loadData();
  }, [authed, loadData]);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const res = await login(password);
      setToken(res.data.token);
      setAuthed(true);
      toast({ title: "Welcome back", description: "Logged in to the admin panel." });
    } catch {
      toast({ title: "Login failed", description: "Incorrect password." });
    }
  };

  const handleLogout = () => {
    clearToken();
    setAuthed(false);
    setPassword("");
  };

  const onFile = (file) => {
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) {
      toast({ title: "Image too large", description: "Please use an image under 4MB." });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, image: reader.result }));
    reader.readAsDataURL(file);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!form.title || !form.image) {
      toast({ title: "Missing details", description: "A title and image are required." });
      return;
    }
    setUploading(true);
    try {
      await addGallery(form);
      toast({ title: "Project added", description: "The gallery has been updated." });
      setForm({ title: "", sector: "Residential", excerpt: "", image: "" });
      loadData();
    } catch {
      toast({ title: "Upload failed", description: "Please try again." });
    } finally {
      setUploading(false);
    }
  };

  const removeGallery = async (id) => {
    await deleteGallery(id);
    setGallery((g) => g.filter((x) => x.id !== id));
  };

  const removeEnquiry = async (id) => {
    await deleteEnquiry(id);
    setEnquiries((e) => e.filter((x) => x.id !== id));
  };

  const replaceProductImage = (productId, file) => {
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) {
      toast({ title: "Image too large", description: "Please use an image under 4MB." });
      return;
    }
    const reader = new FileReader();
    reader.onload = async () => {
      setSavingId(productId);
      try {
        const res = await updateProduct(productId, { image: reader.result });
        setProducts((ps) => ps.map((p) => (p.id === productId ? res.data : p)));
        toast({ title: "Photo updated", description: "The product image has been changed." });
      } catch {
        toast({ title: "Update failed", description: "Please try again." });
      } finally {
        setSavingId(null);
      }
    };
    reader.readAsDataURL(file);
  };

  if (!authed) {
    return (
      <div className="flex min-h-[70vh] items-center justify-center bg-gray-50 px-5">
        <form onSubmit={handleLogin} className="w-full max-w-sm rounded-2xl border border-gray-100 bg-white p-8 shadow-xl">
          <h1 className="font-head text-2xl font-800 text-[#0f2a5a]">Admin Login</h1>
          <div className="ld-accent-bar mt-3" />
          <p className="mt-4 text-sm text-gray-500">Enter your password to manage enquiries and the project gallery.</p>
          <Input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            className="mt-5"
          />
          <button type="submit" className="btn-primary mt-5 w-full rounded-md px-5 py-3 text-sm font-semibold">
            Log In
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-[80vh] bg-gray-50">
      <div className="mx-auto max-w-7xl px-5 py-10 md:px-8">
        <div className="flex items-center justify-between">
          <h1 className="font-head text-2xl font-800 text-[#0f2a5a] md:text-3xl">Admin Dashboard</h1>
          <button onClick={handleLogout} className="flex items-center gap-2 rounded-md border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:border-[#1d4ed8] hover:text-[#1d4ed8]">
            <LogOut size={16} /> Log out
          </button>
        </div>

        <div className="mt-6 flex gap-2">
          {[["enquiries", "Enquiries", Inbox], ["products", "Products", Package]].map(([k, label, Icon]) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              className={`flex items-center gap-2 rounded-md px-4 py-2.5 text-sm font-semibold transition-colors ${
                tab === k ? "bg-[#1d4ed8] text-white" : "border border-gray-200 bg-white text-gray-700 hover:text-[#1d4ed8]"
              }`}
            >
              <Icon size={16} /> {label}
              {k === "enquiries" && enquiries.length > 0 && (
                <span className={`ml-1 rounded-full px-2 py-0.5 text-xs ${tab === k ? "bg-white/20" : "bg-blue-50 text-[#1d4ed8]"}`}>{enquiries.length}</span>
              )}
            </button>
          ))}
        </div>

        {loading && (
          <div className="mt-10 flex items-center gap-2 text-gray-400"><Loader2 className="animate-spin" size={18} /> Loading...</div>
        )}

        {!loading && tab === "enquiries" && (
          <div className="mt-6 space-y-4">
            {enquiries.length === 0 && (
              <div className="rounded-xl border border-dashed border-gray-200 bg-white p-10 text-center text-gray-400">
                No enquiries yet.
              </div>
            )}
            {enquiries.map((e) => (
              <div key={e.id} className="rounded-xl border border-gray-100 bg-white p-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="font-head font-700 text-[#0f2a5a]">{e.name}
                      {e.subject && <span className="ml-2 rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-semibold text-[#1d4ed8]">{e.subject}</span>}
                    </p>
                    <div className="mt-1 flex flex-wrap gap-x-4 text-sm text-gray-500">
                      <a href={`mailto:${e.email}`} className="flex items-center gap-1 hover:text-[#1d4ed8]"><Mail size={13} /> {e.email}</a>
                      {e.phone && <span>{e.phone}</span>}
                      <span>{new Date(e.created_at).toLocaleString("en-GB")}</span>
                    </div>
                  </div>
                  <button onClick={() => removeEnquiry(e.id)} className="rounded-md p-2 text-gray-400 hover:bg-red-50 hover:text-red-500">
                    <Trash2 size={16} />
                  </button>
                </div>
                <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-gray-700">{e.message}</p>
              </div>
            ))}
          </div>
        )}

        {!loading && tab === "gallery" && (
          <div className="mt-6 grid gap-8 lg:grid-cols-[360px,1fr]">
            <form onSubmit={handleUpload} className="h-fit rounded-2xl border border-gray-100 bg-white p-6">
              <h3 className="font-head text-lg font-700 text-[#0f2a5a]">Add a Project</h3>
              <div className="ld-accent-bar mt-3" />
              <label className="mt-5 block text-sm font-medium text-gray-700">Title</label>
              <Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="Project title" className="mt-1.5" />
              <label className="mt-4 block text-sm font-medium text-gray-700">Sector</label>
              <Select value={form.sector} onValueChange={(v) => setForm((f) => ({ ...f, sector: v }))}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Residential">Residential</SelectItem>
                  <SelectItem value="Commercial">Commercial</SelectItem>
                  <SelectItem value="Education">Education</SelectItem>
                  <SelectItem value="Healthcare">Healthcare</SelectItem>
                </SelectContent>
              </Select>
              <label className="mt-4 block text-sm font-medium text-gray-700">Description</label>
              <Textarea value={form.excerpt} onChange={(e) => setForm((f) => ({ ...f, excerpt: e.target.value }))} placeholder="Short description" rows={3} className="mt-1.5" />
              <label className="mt-4 block text-sm font-medium text-gray-700">Photo</label>
              <label className="mt-1.5 flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-200 p-5 text-center text-sm text-gray-500 hover:border-[#1d4ed8]">
                <Upload size={20} className="mb-2 text-[#1d4ed8]" />
                {form.image ? "Change photo" : "Click to choose a photo"}
                <input type="file" accept="image/*" className="hidden" onChange={(e) => onFile(e.target.files[0])} />
              </label>
              {form.image && <img src={form.image} alt="preview" className="mt-3 h-36 w-full rounded-lg object-cover" />}
              <button type="submit" disabled={uploading} className="btn-primary mt-5 w-full rounded-md px-5 py-3 text-sm font-semibold disabled:opacity-60">
                {uploading ? "Uploading..." : "Add to Gallery"}
              </button>
            </form>

            <div className="grid gap-5 sm:grid-cols-2">
              {gallery.map((g) => (
                <div key={g.id} className="overflow-hidden rounded-xl border border-gray-100 bg-white">
                  <div className="relative h-44">
                    <img src={g.image} alt={g.title} className="h-full w-full object-cover" />
                    <button onClick={() => removeGallery(g.id)} className="absolute right-2 top-2 rounded-md bg-white/90 p-2 text-red-500 shadow hover:bg-red-500 hover:text-white">
                      <Trash2 size={15} />
                    </button>
                  </div>
                  <div className="p-4">
                    <span className="text-xs font-semibold uppercase tracking-wider text-[#1d4ed8]">{g.sector}</span>
                    <p className="mt-1 font-head font-700 text-[#0f2a5a]">{g.title}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {!loading && tab === "products" && (
          <div className="mt-6">
            <p className="mb-5 text-sm text-gray-500">
              Replace the photo shown for each product on the Products page. Click a product's
              button and choose a new photo (under 4MB).
            </p>
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {products.map((p) => (
                <div key={p.id} className="overflow-hidden rounded-xl border border-gray-100 bg-white">
                  <div className="relative h-44 bg-gray-100">
                    <img src={p.image} alt={p.name} className="h-full w-full object-cover" />
                    {savingId === p.id && (
                      <div className="absolute inset-0 flex items-center justify-center bg-white/70">
                        <Loader2 className="animate-spin text-[#1d4ed8]" size={22} />
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <span className="text-xs font-semibold uppercase tracking-wider text-[#1d4ed8]">{p.category}</span>
                    <p className="mt-1 font-head font-700 text-[#0f2a5a]">{p.name}</p>
                    <label className="mt-3 flex cursor-pointer items-center justify-center gap-2 rounded-md border border-gray-200 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:border-[#1d4ed8] hover:text-[#1d4ed8]">
                      <RefreshCw size={15} /> Replace photo
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => replaceProductImage(p.id, e.target.files[0])}
                      />
                    </label>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;
